import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, defer, of } from 'rxjs';
import { map, catchError, tap, finalize } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { formatGeoCoordinateForInput } from '../validators/geo-coordinate.validators';
import {
  filterUnknownCountryOptionsetItems,
  normalizeOptionsetItemList,
  normalizeUserRoleOptionsetItemList,
  optionsetListByName,
  OPTIONSET_NAME_SSP_REGIONS,
  OPTIONSET_NAME_USER_ROLE,
  type AddEditUserOptionsets,
  type ChartBucket,
  type OverviewDashboardResponse,
  type StationCard,
  type OptionsetRequest,
  type OptionsetResponse,
  type FilterOptions,
  type OptionItem,
  type OverviewFilterPayload
} from '../models/overview.models';
import { AirportCodesStorageService } from './airport-codes-storage.service';

const API_BASE = environment.apiBaseUrl;

/** Empty dashboard when API omits fields or requests fail — no mock data. */
const EMPTY_DASHBOARD: OverviewDashboardResponse = {
  kpis: [],
  analysts: [],
  airportDistribution: [],
  allowanceDistribution: [],
  countrySplit: []
};

/** Raw card from API: may use snake_case or assignee/comments. status/is_active can be boolean. */
interface ApiStationCard extends Partial<StationCard> {
  assignee?: string;
  analyst?: string;
  comments?: string;
  station_name?: string;
  allowance_percent?: number;
  analyst_id?: string;
  location_code?: string;
  country_name?: string;
  state_name?: string;
  assignee_name?: string;
  airport_name?: string;
  pricing_allowance_percentage?: number;
  is_active?: boolean;
  [key: string]: unknown;
}

/** KPI payload from POST /api/v1/airport/overview/cards when response is { data: { ... } } */
interface OverviewCardsData {
  total_airports?: number;
  active_airports?: number;
  inactive_airports?: number;
  total_us_airports?: number;
  active_us_airports?: number;
  inactive_us_airports?: number;
  total_can_airports?: number;
  active_can_airports?: number;
  inactive_can_airports?: number;
  total_assignees?: number;
  active_assignees?: number;
  inactive_assignees?: number;
  [key: string]: unknown;
}

/** Response rows for POST /api/v1/airport/overview/graphs — chart buckets under `data` */
interface OverviewGraphsApiRow {
  label?: string | null;
  value?: number;
}

interface OverviewGraphsApiResponse {
  data?: {
    /** Bar chart: analyst (or Unassigned) × count of revenue centers — primary field from API */
    revenueDistribution?: OverviewGraphsApiRow[];
    airportsDistribution?: OverviewGraphsApiRow[];
    allowanceDistribution?: OverviewGraphsApiRow[];
    countrySplit?: OverviewGraphsApiRow[];
    airportDistribution?: OverviewGraphsApiRow[];
  };
  [key: string]: unknown;
}

function mapGraphRows(rows: OverviewGraphsApiRow[] | null | undefined): ChartBucket[] {
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => ({
    label: r.label != null && String(r.label).trim() !== '' ? String(r.label) : '—',
    value: typeof r.value === 'number' && !Number.isNaN(r.value) ? r.value : 0
  }));
}

/** Country split: omit rows whose label is null/empty (do not show a "—" slice). */
function mapCountrySplitRows(rows: OverviewGraphsApiRow[] | null | undefined): ChartBucket[] {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((r) => r.label != null && String(r.label).trim() !== '')
    .map((r) => ({
      label: String(r.label),
      value: typeof r.value === 'number' && !Number.isNaN(r.value) ? r.value : 0
    }));
}

/** Revenue centers by analyst: null/empty label → "Unassigned", then sorts so "Unassigned" is last. */
function mapRevenueDistributionRows(rows: OverviewGraphsApiRow[] | null | undefined): ChartBucket[] {
  if (!Array.isArray(rows)) return [];
  const mapped = rows.map((r) => ({
    label: r.label != null && String(r.label).trim() !== '' ? String(r.label) : 'Unassigned',
    value: typeof r.value === 'number' && !Number.isNaN(r.value) ? r.value : 0
  }));
  return [...mapped].sort((a, b) => (a.label === 'Unassigned' ? 1 : b.label === 'Unassigned' ? -1 : 0));
}

/** Raw dashboard from POST /api/v1/airport/overview/cards (may be wrapped in data or flat) */
interface ApiDashboardResponse {
  data?: OverviewCardsData;
  kpis?: OverviewDashboardResponse['kpis'];
  analysts?: OverviewDashboardResponse['analysts'];
  assignees?: OverviewDashboardResponse['analysts'];
  airportDistribution?: OverviewDashboardResponse['airportDistribution'];
  allowanceDistribution?: OverviewDashboardResponse['allowanceDistribution'];
  countrySplit?: OverviewDashboardResponse['countrySplit'];
  [key: string]: unknown;
}

function mapOverviewCardsDataToKpis(data: OverviewCardsData): OverviewDashboardResponse['kpis'] {
  const n = (v: unknown) => (typeof v === 'number' && !Number.isNaN(v) ? v : 0);
  return [
    { title: 'Airports', value: n(data.total_airports), active: n(data.active_airports), inactive: n(data.inactive_airports), icon: 'assets/icons/airports-icon.svg' },
    { title: 'Analysts', value: n(data.total_assignees), active: n(data.active_assignees), inactive: n(data.inactive_assignees), icon: 'assets/icons/analyst-icon.svg' },
    { title: 'USA', value: n(data.total_us_airports), active: n(data.active_us_airports), inactive: n(data.inactive_us_airports), icon: 'assets/icons/usa-flag-icon.svg' },
    { title: 'Canada', value: n(data.total_can_airports), active: n(data.active_can_airports), inactive: n(data.inactive_can_airports), icon: 'assets/icons/canada-flag-icon.svg' }
  ];
}

function normalizeStatusFromApi(raw: Record<string, unknown>, api: ApiStationCard): StationCard['status'] {
  const status = raw['status'] ?? raw['is_active'] ?? api.status;
  if (status === true || status === 'true' || status === 'True') return 'Active';
  if (status === false || status === 'false' || status === 'False') return 'Inactive';
  if (status === 'Active' || status === 'Inactive' || status === 'Closed') return status as StationCard['status'];
  if (typeof status === 'string') {
    const compact = status.trim().toLowerCase().replace(/\s+/g, '');
    if (compact === 'inactive') return 'Inactive';
    if (compact === 'active') return 'Active';
    if (compact === 'closed') return 'Closed';
  }
  return 'Active';
}

/** Map API country / ISO-style codes to filter values USA | Canada (matches get_optionset labels US/CAN → dropdown). */
function normalizeCountryForCard(country: string | undefined | null): 'USA' | 'Canada' {
  const c = String(country ?? '').trim();
  const upper = c.toUpperCase();
  if (upper === 'USA' || upper === 'US') return 'USA';
  if (upper === 'CANADA' || upper === 'CA' || upper === 'CAN') return 'Canada';
  if (!c) return 'USA';
  return 'USA';
}

/**
 * Build POST body for /airport/overview/cards and /graphs.
 * Uses optionset `value` for country/analyst when set; `All` → empty array (no filter).
 */
export function buildOverviewApiFilterPayload(
  country: 'All' | 'USA' | 'Canada',
  analystId: 'All' | string,
  filterOptions: FilterOptions
): OverviewFilterPayload {
  const analyst: string[] = analystId === 'All' ? [] : [String(analystId).trim()];

  if (country === 'All') {
    return { country: [], analyst };
  }
  const v = findCountryOptionValueForOverview(filterOptions, country);
  return { country: [v ?? country], analyst };
}

function findCountryOptionValueForOverview(
  opts: FilterOptions,
  c: 'USA' | 'Canada'
): string | undefined {
  for (const o of opts.countries) {
    const label = String(o.label ?? '').trim();
    const u = label.toUpperCase();
    if (c === 'USA') {
      if (label === 'USA' || label === 'US' || u === 'US' || /united\s*states/i.test(label)) {
        return o.value;
      }
    }
    if (c === 'Canada') {
      if (label === 'Canada' || label === 'CAN' || label === 'CA' || u === 'CAN' || u === 'CA') {
        return o.value;
      }
    }
  }
  return undefined;
}

/**
 * Extract airport/card rows from various API envelopes (POST overview/cards, GET /airport).
 */
function extractCardsArrayFromResponse(res: unknown): unknown[] {
  if (res == null) return [];
  if (Array.isArray(res)) return res;
  if (typeof res !== 'object') return [];
  const obj = res as Record<string, unknown>;

  const pick = (v: unknown): unknown[] | null => (Array.isArray(v) ? v : null);
  for (const key of ['cards', 'airports', 'airport_list', 'results', 'items', 'data', 'airport'] as const) {
    const a = pick(obj[key]);
    if (a) return a;
  }

  const data = obj['data'];
  if (Array.isArray(data)) return data;
  if (data != null && typeof data === 'object' && !Array.isArray(data)) {
    const d = data as Record<string, unknown>;
    for (const key of ['cards', 'airports', 'airport_list', 'results', 'items', 'airports'] as const) {
      const a = pick(d[key]);
      if (a) return a;
    }
  }
  return [];
}

/** Parse POST /api/v1/airport/overview/cards response (KPIs + cards list). */
function parseOverviewCardsPostResponse(res: unknown): {
  dashboard: OverviewDashboardResponse;
  cards: StationCard[];
} {
  const obj = res as Record<string, unknown>;
  const data = (obj['data'] ?? obj) as Record<string, unknown>;
  const inner = data['data'];
  const kpiSource =
    inner != null && typeof inner === 'object' && !Array.isArray(inner)
      ? (inner as Record<string, unknown>)
      : data;

  let list = extractCardsArrayFromResponse(res);
  if (list.length === 0) {
    list = extractCardsArrayFromResponse(kpiSource);
  }
  if (list.length === 0) {
    list = extractCardsArrayFromResponse(data);
  }

  const cards = list.map((item, i) => mapApiCardToStationCard((item ?? {}) as ApiStationCard, i));

  const overviewData = kpiSource as OverviewCardsData;
  const kpisFromData =
    overviewData != null && typeof overviewData.total_airports === 'number'
      ? mapOverviewCardsDataToKpis(overviewData)
      : null;

  const dashboard: OverviewDashboardResponse = {
    kpis: kpisFromData ?? (obj['kpis'] as OverviewDashboardResponse['kpis']) ?? [],
    analysts:
      (obj['analysts'] as OverviewDashboardResponse['analysts']) ??
      (obj['assignees'] as OverviewDashboardResponse['analysts']) ??
      (data['analysts'] as OverviewDashboardResponse['analysts']) ??
      [],
    airportDistribution:
      (obj['airportDistribution'] as OverviewDashboardResponse['airportDistribution']) ??
      (data['airportDistribution'] as OverviewDashboardResponse['airportDistribution']) ??
      [],
    allowanceDistribution:
      (obj['allowanceDistribution'] as OverviewDashboardResponse['allowanceDistribution']) ??
      (data['allowanceDistribution'] as OverviewDashboardResponse['allowanceDistribution']) ??
      [],
    countrySplit:
      (obj['countrySplit'] as OverviewDashboardResponse['countrySplit']) ??
      (data['countrySplit'] as OverviewDashboardResponse['countrySplit']) ??
      []
  };

  return { dashboard, cards };
}

function mapApiCardToStationCard(api: ApiStationCard, index?: number): StationCard {
  const raw = api as Record<string, unknown>;
  const airportId = raw['airport_id'] != null ? String(raw['airport_id']) : (raw['id'] != null ? String(raw['id']) : '');
  const id = airportId || (api.id ? String(api.id) : (index != null ? `card-${index}` : ''));
  const stationName = String(raw['airport_name'] ?? raw['station_name'] ?? api.stationName ?? raw['name'] ?? '');
  const country = (raw['country'] ?? api.country) as string;
  const countryNorm = normalizeCountryForCard(country);
  const status = normalizeStatusFromApi(raw, api);
  const allowancePercent = Number(raw['pricing_allowance_percentage'] ?? raw['allowance_percent'] ?? api.allowancePercent ?? raw['pricing_allowance'] ?? 0);
  
  // Handle assignee field: can be object {id, name} or string
  const assigneeRaw = raw['assignee'] ?? api.assignee;
  let analystId = '';
  let assigneeName: string | undefined = undefined;
  
  if (assigneeRaw != null) {
    if (typeof assigneeRaw === 'object' && assigneeRaw !== null && !Array.isArray(assigneeRaw)) {
      // assignee is an object with id/name
      const assigneeObj = assigneeRaw as Record<string, unknown>;
      analystId = String(assigneeObj['id'] ?? assigneeObj['analyst_id'] ?? assigneeObj['value'] ?? '');
      assigneeName = assigneeObj['name'] != null ? String(assigneeObj['name']) : (assigneeObj['label'] != null ? String(assigneeObj['label']) : undefined);
    } else if (typeof assigneeRaw === 'string') {
      // assignee is a string (name)
      assigneeName = assigneeRaw;
    }
  }
  
  // Fallback to other fields if assignee didn't provide values
  if (!analystId) {
    analystId = String(raw['analyst_id'] ?? api.analystId ?? '');
  }
  if (!assigneeName) {
    assigneeName = raw['assignee_name'] != null ? String(raw['assignee_name']) : (api.analyst != null ? String(api.analyst) : (api.assigneeName != null ? String(api.assigneeName) : undefined));
  }
  
  const notes = raw['comments'] ?? api.notes;
  return {
    id,
    airport_id: airportId || undefined,
    stationName,
    countryCode: String(raw['country_code'] ?? raw['countryCode'] ?? api.countryCode ?? '').trim() || undefined,
    country: countryNorm as 'USA' | 'Canada',
    status,
    allowancePercent,
    analystId,
    locationCode: String(raw['location_code'] ?? raw['airport_code'] ?? api.locationCode ?? raw['iata_code'] ?? api.iataCode ?? ''),
    countryName: String(raw['country_name'] ?? api.countryName ?? ''),
    stateName: String(raw['state_name'] ?? api.stateName ?? ''),
    assigneeName,
    notes: notes != null ? String(notes) : undefined,
    iataCode: String(raw['iata_code'] ?? raw['airport_code'] ?? api.iataCode ?? ''),
    longitude: formatGeoCoordinateForInput(raw['longitude'] ?? api.longitude),
    latitude: formatGeoCoordinateForInput(raw['latitude'] ?? api.latitude),
    search_radius_miles:
      raw['search_radius_miles'] != null
        ? String(raw['search_radius_miles'])
        : raw['search_radius'] != null
          ? String(raw['search_radius'])
          : api.search_radius_miles
  };
}

export type OverviewGraphsResult = {
  /** Formerly airport distribution; API now returns `revenueDistribution` (analyst × # of revenue centers). */
  revenueDistribution: ChartBucket[];
  allowanceDistribution: ChartBucket[];
  countrySplit: ChartBucket[];
};

export type OverviewCardsPostResult = {
  dashboard: OverviewDashboardResponse;
  cards: StationCard[];
};

function mapOverviewGraphsResponseBody(res: OverviewGraphsApiResponse | null | undefined): OverviewGraphsResult {
  const d = (res?.data ?? {}) as {
    revenueDistribution?: OverviewGraphsApiRow[];
    airportsDistribution?: OverviewGraphsApiRow[];
    airportDistribution?: OverviewGraphsApiRow[];
    allowanceDistribution?: OverviewGraphsApiRow[];
    countrySplit?: OverviewGraphsApiRow[];
  };
  const barRows = d.revenueDistribution?.length
    ? d.revenueDistribution
    : d.airportsDistribution?.length
      ? d.airportsDistribution
      : d.airportDistribution;
  return {
    revenueDistribution: mapRevenueDistributionRows(barRows),
    allowanceDistribution: mapGraphRows(d.allowanceDistribution),
    countrySplit: mapCountrySplitRows(d.countrySplit)
  };
}

/** True when POST /graphs body scopes by analyst/assignee (optionset value in `analyst` array). */
function hasAnalystOrAssigneeGraphFilter(payload: OverviewFilterPayload): boolean {
  return (payload.analyst ?? []).some((id) => String(id ?? '').trim() !== '');
}

/** Labels that represent “no analyst / unassigned” on the Revenue Centers Distribution chart. */
function isUnassignedRevenueDistributionLabel(label: string): boolean {
  const t = String(label ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  return (
    t === 'unassigned' ||
    t === 'not assigned' ||
    t === 'n/a' ||
    t === 'na' ||
    t === 'none'
  );
}

/**
 * When an analyst/assignee filter is applied, hide unassigned buckets so the bar chart only shows
 * named analysts relevant to that filter. With no person filter, keep “Unassigned” (from empty API labels).
 */
function stripUnassignedRevenueDistributionWhenFiltered(
  graphs: OverviewGraphsResult,
  payload: OverviewFilterPayload
): OverviewGraphsResult {
  if (!hasAnalystOrAssigneeGraphFilter(payload)) {
    return graphs;
  }
  return {
    ...graphs,
    revenueDistribution: graphs.revenueDistribution.filter((b) => !isUnassignedRevenueDistributionLabel(b.label))
  };
}

@Injectable({ providedIn: 'root' })
export class OverviewApiService {
  private readonly http = inject(HttpClient);
  private readonly airportCodesStorage = inject(AirportCodesStorageService);

  /**
   * POST /api/v1/airport/overview/cards — KPIs + station cards (filters: country, analyst arrays).
   * Overview: use with {@link postOverviewGraphs}; grid rows come from GET /api/v1/airport on first load,
   * and from this endpoint’s `cards` array when filters change (country/analyst).
   */
  postOverviewCards(payload: OverviewFilterPayload): Observable<OverviewCardsPostResult> {
    const url = `${API_BASE}/api/v1/airport/overview/cards`;
    return this.http
      .post<unknown>(url, payload, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
      })
      .pipe(
        map((res) => parseOverviewCardsPostResponse(res)),
        catchError(() =>
          of({
            dashboard: EMPTY_DASHBOARD,
            cards: [] as StationCard[]
          })
        )
      );
  }

  /**
   * GET /api/v1/airport — full airport list; updates {@link AirportCodesStorageService}.
   * Overview initial load uses this response for the data-card grid (with POST cards + graphs in forkJoin).
   * Filter-driven refetches call POST cards + graphs only, not this GET.
   */
  syncAirportCodesFromGet(): Observable<StationCard[]> {
    return defer(() => {
      this.airportCodesStorage.beginAirportListLoad();
      return this.http
        .get<unknown>(`${API_BASE}/api/v1/airport`, {
          headers: { Accept: 'application/json' }
        })
        .pipe(
          map((res) => {
            const list = extractCardsArrayFromResponse(res);
            return list.map((item, i) => mapApiCardToStationCard((item ?? {}) as ApiStationCard, i));
          }),
          tap((cards) => this.airportCodesStorage.replaceFromStationCards(cards)),
          catchError(() => of([] as StationCard[])),
          finalize(() => this.airportCodesStorage.endAirportListLoad())
        );
    });
  }

  /**
   * GET /api/v1/airport — same mapping/storage as {@link syncAirportCodesFromGet}, but failures
   * propagate (does not return an empty list), so callers can retry or show an error.
   */
  fetchAirportListOrThrow(): Observable<StationCard[]> {
    return defer(() => {
      this.airportCodesStorage.beginAirportListLoad();
      return this.http
        .get<unknown>(`${API_BASE}/api/v1/airport`, {
          headers: { Accept: 'application/json' }
        })
        .pipe(
          map((res) => {
            const list = extractCardsArrayFromResponse(res);
            return list.map((item, i) => mapApiCardToStationCard((item ?? {}) as ApiStationCard, i));
          }),
          tap((cards) => this.airportCodesStorage.replaceFromStationCards(cards)),
          finalize(() => this.airportCodesStorage.endAirportListLoad())
        );
    });
  }

  /**
   * POST /api/v1/airport/overview/graphs — bar + doughnut chart buckets (same filter body as cards).
   */
  postOverviewGraphs(payload: OverviewFilterPayload): Observable<OverviewGraphsResult> {
    const url = `${API_BASE}/api/v1/airport/overview/graphs`;
    return this.http
      .post<OverviewGraphsApiResponse>(url, payload, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
      })
      .pipe(
        map((res) =>
          stripUnassignedRevenueDistributionWhenFiltered(mapOverviewGraphsResponseBody(res), payload)
        ),
        catchError(() =>
          of({
            revenueDistribution: [] as ChartBucket[],
            allowanceDistribution: [] as ChartBucket[],
            countrySplit: [] as ChartBucket[]
          })
        )
      );
  }

  /**
   * POST /api/v1/common/get_optionset — fetch filter dropdown options.
   * Bearer token via interceptor.
   * Request: { names: ["Country", "assignee", "Airport_Status"] }
   * Response: { status: "Success", data: { Country: [...], assignee: [...], Airport_Status: [...] } }
   */
  getOptionset(): Observable<FilterOptions> {
    const url = `${API_BASE}/api/v1/common/get_optionset`;
    const payload: OptionsetRequest = {
      names: ['Country', 'assignee', 'Airport_Status']
    };
    return this.http
      .post<OptionsetResponse>(url, payload, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
      })
      .pipe(
        map((res) => {
          const data = res?.data ?? {};
          // Handle case-insensitive key lookup for Country
          const countryKey = Object.keys(data).find((k) => k.toLowerCase() === 'country') ?? 'Country';
          const assigneeKey = Object.keys(data).find((k) => k.toLowerCase() === 'assignee') ?? 'assignee';
          const statusKey = Object.keys(data).find((k) => k.toLowerCase() === 'airport_status') ?? 'Airport_Status';
          const rawCountries = (data[countryKey] ?? data['Country'] ?? []) as OptionItem[];
          return {
            countries: filterUnknownCountryOptionsetItems(rawCountries),
            analysts: (data[assigneeKey] ?? data['assignee'] ?? []) as OptionItem[],
            statuses: (data[statusKey] ?? data['Airport_Status'] ?? []) as OptionItem[]
          };
        }),
        catchError(() =>
          of({
            countries: [],
            analysts: [],
            statuses: []
          })
        )
      );
  }

  /**
   * POST /api/v1/common/get_optionset — arbitrary names and optional fields (e.g. airport_code for Cuisine).
   */
  getOptionsetRaw(payload: OptionsetRequest): Observable<Record<string, OptionItem[]>> {
    const url = `${API_BASE}/api/v1/common/get_optionset`;
    return this.http
      .post<OptionsetResponse>(url, payload, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
      })
      .pipe(
        map((res) => {
          const data = res?.data ?? {};
          const out: Record<string, OptionItem[]> = {};
          for (const key of Object.keys(data)) {
            const arr = data[key];
            if (!Array.isArray(arr)) continue;
            if (key.toLowerCase() === 'country') {
              out[key] = filterUnknownCountryOptionsetItems(arr as OptionItem[]);
            } else {
              out[key] = arr as OptionItem[];
            }
          }
          return out;
        }),
        catchError(() => of({} as Record<string, OptionItem[]>))
      );
  }

  /**
   * POST get_optionset once for Add/Edit User — Region ({@link OPTIONSET_NAME_SSP_REGIONS}) + Title ({@link OPTIONSET_NAME_USER_ROLE}).
   */
  getAddEditUserOptionsets(): Observable<AddEditUserOptionsets> {
    return this.getOptionsetRaw({
      names: [OPTIONSET_NAME_SSP_REGIONS, OPTIONSET_NAME_USER_ROLE]
    }).pipe(
      map((data) => ({
        regions: this.parseSspRegionsFromOptionsetData(data),
        userRoles: this.parseUserRolesFromOptionsetData(data)
      })),
      catchError(() => of({ regions: [], userRoles: [] } satisfies AddEditUserOptionsets))
    );
  }

  /**
   * POST get_optionset with {@link OPTIONSET_NAME_SSP_REGIONS} for Add/Edit User Organization Name.
   */
  getSspRegionsOptions(): Observable<OptionItem[]> {
    return this.getAddEditUserOptionsets().pipe(map((data) => data.regions));
  }

  /**
   * POST get_optionset with {@link OPTIONSET_NAME_USER_ROLE} for Add/Edit User Title.
   */
  getUserRoleOptions(): Observable<OptionItem[]> {
    return this.getAddEditUserOptionsets().pipe(map((data) => data.userRoles));
  }

  private parseSspRegionsFromOptionsetData(data: Record<string, OptionItem[]>): OptionItem[] {
    const list = optionsetListByName(data, OPTIONSET_NAME_SSP_REGIONS);
    return this.normalizeGenericOptionsetList(list);
  }

  private parseUserRolesFromOptionsetData(data: Record<string, OptionItem[]>): OptionItem[] {
    const list = optionsetListByName(data, OPTIONSET_NAME_USER_ROLE);
    const raw = normalizeUserRoleOptionsetItemList(list as unknown[]);
    return this.normalizeGenericOptionsetList(raw);
  }

  private normalizeGenericOptionsetList(items: OptionItem[]): OptionItem[] {
    const raw = normalizeOptionsetItemList(items as unknown[]);
    return raw
      .map((o) => ({
        label: String(o.label ?? '').trim() || String(o.value ?? '').trim(),
        value: String(o.value ?? '').trim() || String(o.label ?? '').trim()
      }))
      .filter((o) => o.value.length > 0)
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }
}
